
import { toState } from '../base/redux';
import { areThereNotifications } from '../notifications';
import { getOverlayToRender } from '../overlay';

/**
 * Tells whether or not the notifications should be displayed within
 * the conference feature based on the current Redux state.
 *
 * @param {Object|Function} stateful - The redux store state.
 * @returns {boolean}
 */
export function shouldDisplayNotifications(stateful) {
    const state = toState(stateful);
    const isAnyOverlayVisible = Boolean(getOverlayToRender(state));
    const { calleeInfoVisible } = state['features/invite'];

    return areThereNotifications(state)
      && !isAnyOverlayVisible
      && !calleeInfoVisible;
}


/**
 *
 * Returns true if polls feature is disabled.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/config.
 * @returns {boolean}
 */
export function arePollsDisabled(stateful) {
    const state = toState(stateful);

    // Polls are available to visitors too (they can see and answer polls; creating remains
    // moderator-only). The only thing that disables polls is the explicit config flag - visitor
    // status must NOT gate polls, otherwise visitors never get the polls tab/pane.
    return Boolean(state['features/base/config']?.disablePolls);
}

/**
 * Resolve the active access token from redux state, falling back to other
 * known sources. Used by the meeting-limit / dashboard URL helpers that need to
 * attach a token to the URL and that must work on web (jitsiLocalStorage) and
 * React Native (no jitsiLocalStorage) without a direct dependency on either.
 *
 * @param {Object} state - The Redux state.
 * @returns {string|undefined}
 */
function _resolveAccessTokenFromState(state) {
    const jwtFeature = state['features/base/jwt'] || {};

    return jwtFeature.accessToken ?? jwtFeature.jwt;
}

/**
 * Build a URL with an optional access_token query parameter.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} options - URL components.
 * @param {string} options.urlPrefix - URL_PREFIX from config.
 * @param {string} options.clientId - MH_CLIENT_ID from config.
 * @param {string} options.redirectPath - Redirect path appended to URL_PREFIX.
 * @param {string} options.deviceType - Either 'web' or 'mobile'.
 * @returns {string}
 */
function _buildSubscriptionUrl(state, { urlPrefix, clientId, redirectPath, deviceType }) {
    const accessToken = _resolveAccessTokenFromState(state);

    return `${urlPrefix}serviceLogin?client_id=${clientId}&redirect_uri=${
        urlPrefix}${redirectPath}&device_type=${deviceType}${accessToken ? `&access_token=${accessToken}` : ''}`;
}

/**
 * Returns the dashboard URL using the redux config. Available on both web and
 * native so the meeting-limit flow can link there.
 *
 * @param {Object|Function} stateful - The redux store state.
 * @returns {string}
 */
export function getDashBoardUrlFromState(stateful) {
    const state = toState(stateful);
    const { URL_PREFIX, MH_CLIENT_ID } = state['features/base/config'] || {};

    return _buildSubscriptionUrl(state, {
        urlPrefix: URL_PREFIX,
        clientId: MH_CLIENT_ID,
        redirectPath: 'selectSubscription',
        deviceType: 'web'
    });
}

/**
 * Returns the subscription / upgrade-plan URL using the redux config. Available
 * on both web and native.
 *
 * @param {Object|Function} stateful - The redux store state.
 * @returns {string}
 */
export function getSubscriptionPageFromState(stateful) {
    const state = toState(stateful);
    const { URL_PREFIX, MH_CLIENT_ID } = state['features/base/config'] || {};

    return _buildSubscriptionUrl(state, {
        urlPrefix: URL_PREFIX,
        clientId: MH_CLIENT_ID,
        redirectPath: 'customer/mysubscription',
        deviceType: 'web'
    });
}

